import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchLetterDetail, updateLetterStatus } from "./lettersApi";
import ThreadTimeline from "./ThreadTimeline";
import FilePreviewModal from "./FilePreviewModal";
import Dashboardnavbar from "../../../layout/Dashboardnavbar"; // ⚠️ verify this path
import { buildRolePath } from "./roleRoutes";
import "./Letterdetail.css";

// Matches the backend permission tier for PATCH /dpr/letters/<id>/status/ —
// super_admin, admin, hod, engineer can toggle status; viewer is read-only.
const STATUS_EDIT_ROLES = ["super_admin", "admin", "hod", "engineer"];

export default function LetterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [letter, setLetter] = useState(null);
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { fileUrl, title, subtitle }

  const currentRole = sessionStorage.getItem("dpr_role") || "super_admin";
  const canEditStatus = STATUS_EDIT_ROLES.includes(currentRole);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchLetterDetail(id)
      .then((res) => {
        if (cancelled) return;
        const { thread, ...letterFields } = res;
        setLetter(letterFields);
        setThread(thread || []);
      })
      .catch((err) => !cancelled && setError(err))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const openFile = (fileUrl, title, subtitle) => setPreview({ fileUrl, title, subtitle });
  const closeFile = () => setPreview(null);

  const [statusUpdating, setStatusUpdating] = useState(false);
  const toggleStatus = async () => {
    const nextStatus = letter.status === "closed" ? "open" : "closed";
    setStatusUpdating(true);
    try {
      const updated = await updateLetterStatus(letter.id, nextStatus);
      // Use the backend's actual returned status as source of truth, not just
      // an optimistic assumption that it matches what we sent.
      setLetter((prev) => ({ ...prev, status: updated?.status ?? nextStatus }));
    } catch (err) {
      // Endpoint may not exist yet on the backend — see the status feature spec.
      console.error("Couldn't update status:", err);
    } finally {
      setStatusUpdating(false);
    }
  };

  // ProjectAllDetails reads the project via location.state (not a URL param) —
  // see ProjectAllDetails.jsx: `location.state?.project?.id`. So this has to pass
  // state, not build a /project/:id URL (that route doesn't exist).
  // workstage_id/submission_id aren't guaranteed on the detail response per the
  // API doc, but ProjectAllDetails doesn't consume them yet anyway — kept here
  // in case deep-linking into a specific workstage gets added later.
  const goToSource = () => {
    if (!letter) return;
    navigate(buildRolePath("ProjectAllDetails"), { state: { project: letter.project } });
  };

  if (loading) return <div className="letters-table-state">Loading letter…</div>;
  if (error || !letter) return <div className="letters-table-state letters-error">Couldn't load this letter.</div>;

  return (
    <>
      <Dashboardnavbar name="Letters Register" url="Letters" />
      <div style={{ padding: "24px 30px" }} className="letter-detail-page">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>
          ← Back to letters register
        </button>

        <div className="letter-card">
          <div className="letter-top">
            <div>
              <h2>{letter.letter_num}</h2>
              <p className="letter-meta">
                {letter.project_details_name} · {letter.section_name}
                {letter.rev_no ? ` · Rev ${letter.rev_no}` : ""} · {letter.date}
              </p>
              {letter.reply_id && (
                <p className="letter-meta letter-reply-note">In reply to a previous revision of this submission</p>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span className={`letters-status-badge letters-status-${(letter.status || "open").toLowerCase()}`}>
                {letter.status ? letter.status[0].toUpperCase() + letter.status.slice(1) : "Open"}
              </span>
              {canEditStatus && (
                <button type="button" className="view-file secondary" onClick={toggleStatus} disabled={statusUpdating}>
                  {statusUpdating ? "…" : letter.status === "closed" ? "Reopen" : "Mark closed"}
                </button>
              )}
              {letter.file && (
                <button
                  type="button"
                  className="view-file"
                  onClick={() => openFile(letter.file, letter.letter_num, "Original submission")}
                >
                  View file
                </button>
              )}
              <button type="button" className="view-file secondary" onClick={goToSource}>
                Open in project
              </button>
            </div>
          </div>

          <p className="letter-desc">{letter.description}</p>

          <div className="meta-grid meta-grid-3">
            <div>
              <span className="label">Workstage</span>
              <span className="val">{letter.workstage_name || "—"}</span>
            </div>
            <div>
              <span className="label">Submitted to</span>
              <span className="val">{letter.submitted_to}</span>
            </div>
            <div>
              <span className="label">Revision</span>
              <span className="val">{letter.rev_no || "—"}</span>
            </div>
          </div>
        </div>

        <div className="thread-section">
          <h3>Correspondence</h3>
          <ThreadTimeline letter={letter} thread={thread} onViewFile={openFile} />
        </div>

        {preview && (
          <FilePreviewModal fileUrl={preview.fileUrl} title={preview.title} subtitle={preview.subtitle} onClose={closeFile} />
        )}
      </div>
    </>
  );
}